import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  useGetTermsAcceptanceStatus,
  useGetTermsAndConditions,
} from "../../services/queries";
import {
  useRecordTermsAcceptance,
  useLeaveEarningsProgram,
  useLogout,
} from "../../services/mutations";
import { PATH } from "../../utils/config";

const DOC_TITLES = {
  tos: "Terms of Use",
  privacy_policy: "Privacy Policy",
  referral_terms: "Earnings Program Terms",
  fa_terms: "Founding Ambassador Terms",
};

const docTitle = (slug) => DOC_TITLES[slug] || "Legal Document";

const getToken = () => {
  try {
    return JSON.parse(localStorage.getItem("keyCloak") || "{}").token || null;
  } catch (e) {
    return null;
  }
};

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia(REDUCED_MOTION_QUERY).matches
  );
  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(REDUCED_MOTION_QUERY);
    const handler = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);
  return reduced;
};

/**
 * A single pending-document card: a scrollable rendering of the current document
 * body plus its affirmative accept action. Referral/FA documents additionally
 * offer an explicit "Leave the Earnings Program" exit so a participant is never
 * permanently locked out of the whole product over a program they no longer want.
 */
const DocCard = ({
  doc,
  html,
  loading,
  onAccept,
  accepting,
  onLeave,
  leaving,
}) => {
  const title = docTitle(doc.slug);
  return (
    <Box
      component="section"
      aria-label={title}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Box
        tabIndex={0}
        aria-label={`${title} content`}
        sx={{
          maxHeight: 240,
          overflowY: "auto",
          p: 1.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "background.default",
          fontSize: 14,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} aria-label="Loading document" />
          </Box>
        ) : html ? (
          <div dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <Typography variant="body2">
            This document is temporarily unavailable. Please try again later or
            contact support@iyuga.net.
          </Typography>
        )}
      </Box>
      {onLeave && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 1.5 }}
        >
          Leaving stops new earning; you can still withdraw what you've earned.
        </Typography>
      )}
      <Box
        sx={{
          mt: onLeave ? 0.5 : 1.5,
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "flex-end",
        }}
      >
        {onLeave && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onLeave}
            disabled={leaving || accepting}
          >
            {leaving ? "Leaving…" : "Leave the Earnings Program"}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onAccept}
          disabled={accepting || leaving || loading || !html}
        >
          {accepting ? "Saving…" : `I accept the ${title}`}
        </Button>
      </Box>
    </Box>
  );
};

/**
 * App-root blocking gate. Renders a non-dismissible, focus-trapped modal when the
 * authenticated user has one or more legal documents pending (re-)acceptance.
 *
 * HARD gate (Gordon's ruling): no app access behind it until the required
 * documents are accepted — at signup-time and at re-acceptance-time alike. Escape
 * and backdrop dismissal are disabled; only accepting every required document (or,
 * for the referral leg, leaving the Earnings Program) clears it. Logout is always
 * available so a user can leave. Fully inert while the backend flag is OFF (the
 * status endpoint then reports nothing pending) — mount this behind the FE flag.
 */
const TermsAcceptanceGate = () => {
  const isAuthed = !!getToken();
  const statusQuery = useGetTermsAcceptanceStatus({ enabled: isAuthed });

  const status = statusQuery.data?.data;
  const pending = status?.pending_documents || [];
  const required = Boolean(isAuthed && status?.acceptance_required && pending.length);
  const hasReferralPending = pending.some((d) => d.is_referral);

  const termsQuery = useGetTermsAndConditions(
    { enabled: required },
    hasReferralPending
  );
  const bodyBySlug = useMemo(() => {
    const results = termsQuery.data?.data?.results || [];
    const map = {};
    results.forEach((d) => {
      map[d.slug] = d.body;
    });
    return map;
  }, [termsQuery.data]);

  const recordMutation = useRecordTermsAcceptance();
  const leaveMutation = useLeaveEarningsProgram();
  const logoutMutation = useLogout();
  const reducedMotion = usePrefersReducedMotion();

  if (!required) return null;

  const leaveApp = () => {
    localStorage.removeItem("keyCloak");
    localStorage.removeItem("persist:root");
    window.location.href = window.location.origin + (PATH.LOGIN || "/login");
  };
  const handleLogout = () => {
    logoutMutation.mutate(null, { onSuccess: leaveApp, onError: leaveApp });
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      disableEscapeKeyDown
      onClose={() => {
        /* HARD gate: ignore backdrop/escape — the modal cannot be dismissed. */
      }}
      transitionDuration={reducedMotion ? 0 : undefined}
      aria-labelledby="terms-gate-title"
      aria-describedby="terms-gate-desc"
    >
      <DialogTitle id="terms-gate-title">
        Please review and accept to continue
      </DialogTitle>
      <DialogContent dividers>
        <Typography id="terms-gate-desc" variant="body2" sx={{ mb: 2 }}>
          To keep using iYuga you must review and accept the current version of
          the document{pending.length > 1 ? "s" : ""} below. You can log out at
          any time.
        </Typography>
        {pending.map((doc) => (
          <DocCard
            key={doc.slug}
            doc={doc}
            html={bodyBySlug[doc.slug]}
            loading={termsQuery.isLoading}
            onAccept={() => recordMutation.mutate(doc.slug)}
            accepting={recordMutation.isLoading}
            onLeave={
              doc.is_referral ? () => leaveMutation.mutate() : null
            }
            leaving={leaveMutation.isLoading}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogout} color="inherit">
          Log out
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAcceptanceGate;
